import { useState, useCallback } from 'react';
import { pOrderLineService } from '../../../../../services/pOrderLineService';
import { stcTransService, StcTransOrderDto } from '../../../../../services/stcTransService';
import { POrderLineWithHeaderDto } from '../../../../../types/purchaseOrderLines';
import { POrderLineDto } from '../../../../../types/porders';
import { processBarcodeInput } from '../helpers';

/**
 * useFreeReceiving — fri-modus-logikk for varemottak (skann vilkårlig vare på tvers av åpne
 * innkjøpsordrer). Isolerer skann/søk, velgmodal-state og BatchID-håndtering (auto-gjenoppta)
 * UT av den store POrderReceivingDetail-komponenten.
 *
 * Eierskap: ÉN instans opprettes i POrderReceivingDetail og deles til FreeReceivingScanPanel.
 * - Containeren kaller resumeActiveBatch()/ensureBatchId() for å styre sin egen batchId-state.
 * - ScanPanel rendrer søkefelt + velgmodal basert på state og kaller handleScan()/chooseLine().
 *
 * Ingen effekt med mindre `enabled` er true (kun receivingMode='free').
 */

export interface ScannedBatchInfo {
  batchNo?: string;
  expDate?: string;
  quantity?: number;
}

export interface UseFreeReceivingOptions {
  /** True kun i receivingMode='free'. Når false er alle handlinger no-ops. */
  enabled: boolean;
  /**
   * Kalles når en innkjøpsordrelinje er valgt — automatisk ved ett treff, eller fra velgmodalen.
   * Containeren mapper til sin egen selectedSearchLine/newReceipt-flyt.
   */
  onSelectLine: (line: POrderLineWithHeaderDto, barcode: ScannedBatchInfo | null) => void;
}

export interface UseFreeReceivingResult {
  // --- BatchID-håndtering (styres av containeren) ---
  /** Gjenoppta operatørens aktive frie mottak, eller null hvis ingen finnes. */
  resumeActiveBatch: () => Promise<StcTransOrderDto | null>;
  /** Hent eksisterende eller opprett ny BatchID for fritt mottak (resume-first). */
  ensureBatchId: () => Promise<string>;

  // --- Skann/søk-state (rendres av ScanPanel) ---
  searchInput: string;
  setSearchInput: (v: string) => void;
  isSearching: boolean;
  /** Satt når søket ga 0 treff — inneholder varenr/strekkode det ble søkt på. */
  notFoundItemNo: string | null;
  /** Satt ved teknisk feil under søk (melding). */
  searchError: string | null;
  clearSearchError: () => void;
  matchingLines: POrderLineWithHeaderDto[];
  showLineSelector: boolean;
  closeLineSelector: () => void;
  scannedBatchInfo: ScannedBatchInfo | null;

  // --- Handlinger ---
  /**
   * Kjør et skann/søk på tvers av åpne PO-linjer.
   * 0 treff → setter searchError. 1 treff → onSelectLine. Flere treff → åpner velgmodal.
   */
  handleScan: (scanInput: string) => Promise<void>;
  /** Velg en linje fra velgmodalen (eller programmatisk). Kaller onSelectLine. */
  chooseLine: (line: POrderLineWithHeaderDto) => void;
}

/**
 * Mapper et søkeresultat (POrderLineWithHeaderDto) til POrderLineDto som
 * POrderReceivingDetail bruker internt (selectedSearchLine / availableOrderLines).
 */
export function mapHeaderLineToOrderLine(line: POrderLineWithHeaderDto): POrderLineDto {
  return {
    id: line.id,
    companyNo: line.companyNo,
    orderNo: line.orderNo,
    lineNo: line.lineNo,
    lineNoERP: line.lineNoERP,
    lineStatus: line.lineStatus,
    itemNo: line.itemNo,
    itemBarCode: line.itemBarCode,
    description1: line.description1,
    itemGroup1: line.itemGroup1,
    itemGroup2: line.itemGroup2,
    itemGroup3: line.itemGroup3,
    quantity: line.quantity,
    quantityDelivered: line.quantityDelivered,
    itemUnitCode: line.itemUnitCode,
    itemUnitTxt: line.itemUnitTxt,
    price: line.price,
    discount: line.discount,
    deliveryDate: line.deliveryDate ? new Date(line.deliveryDate) : undefined,
    serialNoItemYesNo: line.serialNoItemYesNo,
    stdLocationName: line.stdLocationName,
    warehouse: line.warehouse,
    orderNoERP: line.orderNoERP,
    itemBatchNo: line.itemBatchNo,
    batchItemYesNo: line.batchItemYesNo,
    orderLineFreeField1: line.orderLineFreeField1,
    orderLineFreeField2: line.orderLineFreeField2,
    orderLineFreeField3: line.orderLineFreeField3,
    inboundShipmentNo: line.inboundShipmentNo,
    remainingQuantity: line.remainingQuantity,
    itemExistsInMaster: line.itemExistsInMaster,
  };
}

export function useFreeReceiving(options: UseFreeReceivingOptions): UseFreeReceivingResult {
  const { enabled, onSelectLine } = options;

  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [notFoundItemNo, setNotFoundItemNo] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [matchingLines, setMatchingLines] = useState<POrderLineWithHeaderDto[]>([]);
  const [showLineSelector, setShowLineSelector] = useState(false);
  const [scannedBatchInfo, setScannedBatchInfo] = useState<ScannedBatchInfo | null>(null);

  const resumeActiveBatch = useCallback(async (): Promise<StcTransOrderDto | null> => {
    if (!enabled) return null;
    return await stcTransService.getActiveFreeReceiving();
  }, [enabled]);

  const ensureBatchId = useCallback(async (): Promise<string> => {
    return await stcTransService.getOrCreateBatchIdForOpenReceiving();
  }, []);

  const clearSearchError = useCallback(() => {
    setSearchError(null);
    setNotFoundItemNo(null);
  }, []);
  const closeLineSelector = useCallback(() => {
    setShowLineSelector(false);
    setMatchingLines([]);
  }, []);

  const chooseLine = useCallback((line: POrderLineWithHeaderDto) => {
    setShowLineSelector(false);
    onSelectLine(line, scannedBatchInfo);
  }, [onSelectLine, scannedBatchInfo]);

  const handleScan = useCallback(async (scanInput: string) => {
    if (!enabled) return;

    const parsed = processBarcodeInput(scanInput);
    if (!parsed) return;

    setIsSearching(true);
    setSearchError(null);
    setNotFoundItemNo(null);

    const barcode: ScannedBatchInfo = {
      batchNo: parsed.batchNo,
      expDate: parsed.expiryDate,
      quantity: parsed.quantity,
    };

    try {
      const results = await pOrderLineService.searchOpenPOLinesByItem(parsed.itemNo);

      if (results.length === 0) {
        setMatchingLines([]);
        setScannedBatchInfo(null);
        setNotFoundItemNo(parsed.itemNo);
      } else if (results.length === 1) {
        // Ett treff — auto-velg
        setScannedBatchInfo(barcode);
        onSelectLine(results[0], barcode);
      } else {
        // Flere treff — vis velgmodal (behold barcode-info til linjen velges)
        setScannedBatchInfo(barcode);
        setMatchingLines(results);
        setShowLineSelector(true);
      }
    } catch (err) {
      console.error('Error searching open PO lines:', err);
      setSearchError(err instanceof Error ? err.message : 'unknown');
    } finally {
      setIsSearching(false);
    }
  }, [enabled, onSelectLine]);

  return {
    resumeActiveBatch,
    ensureBatchId,
    searchInput,
    setSearchInput,
    isSearching,
    notFoundItemNo,
    searchError,
    clearSearchError,
    matchingLines,
    showLineSelector,
    closeLineSelector,
    scannedBatchInfo,
    handleScan,
    chooseLine,
  };
}
